// ── 동기화 응답 검증 & 체인 집계 공용 모듈 (React/네트워크 의존 없음) ─────
// useDrawManager 내부의 응답 검증(parseApiResponse)과 청크 체인 집계(runChunked 의
// 집계 부분)를 순수 함수로 분리해 단위 테스트할 수 있게 한다.

export type ApiStatus = 'success' | 'partial' | 'failed' | 'running' | 'error' | 'aborted';
export type ChainStatus = 'success' | 'partial' | 'failed' | 'running' | 'error' | 'aborted';

export interface ApiBatchResult {
  status: ApiStatus;
  failedRounds: number[];
  attempted: number;
  inserted: number;
  fetched: number;
  failed: number;
  msg: string;
}

export interface SyncChunk {
  s: number;
  e: number;
}

export interface ChainResult {
  status: ChainStatus;
  failedRounds: number[];
  attempted: number;
  fetched: number;
  inserted: number;
  failed: number;
  successChunks: number;
  totalChunks: number;
  untouchedRounds: number[];
}

export function roundsLabel(rounds: number[]): string {
  if (rounds.length === 0) return '';
  const first = rounds[0];
  const last = rounds[rounds.length - 1];
  return first === last ? `${first}회차` : `${first}~${last}회차`;
}

function isNonNegInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0;
}

/**
 * fetch-lotto-history 응답을 검증한다.
 * - status ∈ {success, partial, failed} (running 은 별도)
 * - attempted === 요청 회차 수, 0 <= inserted <= fetched <= attempted
 * - inserted + failed === attempted
 * - failedRounds 중복 없음·요청 범위 내·개수 일치
 * - 상태별: success(failed=0, inserted=attempted), partial(0<inserted<attempted),
 *   failed(inserted=0, failed=attempted)
 * 위반 시 일반 error 를 반환해 성공/부분 성공으로 오판하지 않는다.
 */
export function parseApiResponse(data: unknown, expectedRounds: number[]): ApiBatchResult {
  if (!data || typeof data !== 'object') {
    return { status: 'error', failedRounds: [], attempted: 0, inserted: 0, fetched: 0, failed: 0, msg: '서버 응답이 없습니다.' };
  }
  const d = data as Record<string, unknown>;
  const status = d.status as string | undefined;

  if (status === 'running') {
    return { status: 'running', failedRounds: [], attempted: 0, inserted: 0, fetched: 0, failed: 0, msg: '이미 다른 동기화가 진행 중입니다.' };
  }

  const label = roundsLabel(expectedRounds);

  const attempted = isNonNegInt(d.attempted) ? d.attempted : -1;
  const inserted = isNonNegInt(d.inserted) ? d.inserted : -1;
  const fetched = isNonNegInt(d.fetched) ? d.fetched : -1;
  const failed = isNonNegInt(d.failed) ? d.failed : -1;

  const expectedSet = new Set(expectedRounds);
  let failedRounds: number[] = [];
  let failedRoundsValid = Array.isArray(d.failedRounds);
  if (failedRoundsValid) {
    const seen = new Set<number>();
    for (const n of d.failedRounds as unknown[]) {
      if (typeof n !== 'number' || !Number.isInteger(n) || !expectedSet.has(n) || seen.has(n)) {
        failedRoundsValid = false;
        break;
      }
      seen.add(n);
      failedRounds.push(n);
    }
  }

  const elapsedSec = typeof d.elapsedMs === 'number' ? Math.round(d.elapsedMs / 1000) : 0;

  const commonOk =
    attempted >= 0 &&
    inserted >= 0 &&
    fetched >= 0 &&
    failed >= 0 &&
    attempted === expectedRounds.length &&
    inserted <= fetched &&
    fetched <= attempted &&
    inserted + failed === attempted &&
    failedRoundsValid &&
    failedRounds.length === failed;

  const invalid = (): ApiBatchResult => ({
    status: 'error', failedRounds: [], attempted: 0, inserted: 0, fetched: 0, failed: 0, msg: '비정상 서버 응답입니다.',
  });

  if (status === 'success') {
    if (!commonOk || failed !== 0 || failedRounds.length !== 0 || inserted !== attempted) return invalid();
    return { status: 'success', failedRounds, attempted, inserted, fetched, failed, msg: `${label} 완료 (${inserted}건 등록, ${elapsedSec}초)` };
  }
  if (status === 'partial') {
    if (!commonOk || !(inserted > 0 && inserted < attempted) || !(failed > 0 && failed < attempted)) return invalid();
    return { status: 'partial', failedRounds, attempted, inserted, fetched, failed, msg: `${label} 일부 성공 (${inserted}건 등록, ${failed}건 실패)` };
  }
  if (status === 'failed') {
    if (!commonOk || inserted !== 0 || failed !== attempted) return invalid();
    return { status: 'failed', failedRounds, attempted, inserted, fetched, failed, msg: `${label} 실패 (${failed}건)` };
  }
  return invalid();
}

/**
 * 청크 체인 집계. runChunked 와 동일한 규칙으로 순수 계산한다.
 * - results 는 chunks 와 1:1 대응하되, 중단(aborted/running/error) 시 이후 미실행 청크는
 *   results 에 없거나 그 자리에 중단 상태가 온다.
 * - error/running/aborted 이후 청크는 untouchedRounds 로 보존
 * - 미시도 회차·실패 회차는 중복 제거
 */
export function aggregateSyncChain(chunks: SyncChunk[], results: ApiBatchResult[]): ChainResult {
  const failedRounds: number[] = [];
  const untouchedRounds: number[] = [];
  let successChunks = 0;
  let attempted = 0;
  let fetched = 0;
  let inserted = 0;
  let failed = 0;
  let stopReason: 'aborted' | 'running' | 'error' | null = null;

  const markUntouchedFrom = (fromIndex: number) => {
    for (let j = fromIndex; j < chunks.length; j++) {
      for (let r = chunks[j].s; r <= chunks[j].e; r++) untouchedRounds.push(r);
    }
  };

  for (let i = 0; i < chunks.length; i++) {
    const res = results[i];
    if (!res) {
      // 이전 청크에서 중단되어 실행되지 않은 경우
      stopReason = stopReason ?? 'error';
      markUntouchedFrom(i);
      break;
    }

    if (res.status === 'success' || res.status === 'partial' || res.status === 'failed') {
      attempted += res.attempted;
      fetched += res.fetched;
      inserted += res.inserted;
      failed += res.failed;
      if (res.status === 'success') successChunks += 1;
      if (res.status === 'partial' || res.status === 'failed') failedRounds.push(...res.failedRounds);
    } else if (res.status === 'running') {
      stopReason = 'running';
      markUntouchedFrom(i);
      break;
    } else if (res.status === 'aborted') {
      stopReason = 'aborted';
      markUntouchedFrom(i);
      break;
    } else {
      stopReason = 'error';
      markUntouchedFrom(i);
      break;
    }
  }

  const dedupFailed = [...new Set(failedRounds)].sort((a, b) => a - b);
  const dedupUntouched = [...new Set(untouchedRounds)].sort((a, b) => a - b);

  let status: ChainStatus;
  if (stopReason === 'aborted') status = 'aborted';
  else if (stopReason === 'running') status = 'running';
  else if (stopReason === 'error') status = 'error';
  else if (failed === 0) status = 'success';
  else if (inserted === 0) status = 'failed';
  else status = 'partial';

  return {
    status,
    failedRounds: dedupFailed,
    attempted,
    fetched,
    inserted,
    failed,
    successChunks,
    totalChunks: chunks.length,
    untouchedRounds: dedupUntouched,
  };
}